import { useMemo } from 'react';
import { BuyerGroup, sortTakerMarkets } from '../../data/useGroupByBuyer';
import { TakerMarket } from '../../data/useTakerMarkets';

interface BuyerCurrencyStats {
  avgDaysPaidEarly: number;
  nextGoodFundsDateWithEligibleInvoices: string | null;
  totalInvoiceAmount: number;
}

interface BuyerCurrencyAccumulator extends BuyerCurrencyStats {
  eligibleDpeWeightedSum: number;
  eligibleInvoiceAmount: number;
}

// Calculate stats across taker markets for a buyer currency group used in Invoice Central
// avgDaysPaidEarly and nextGoodFundsDateWithEligibleInvoices used on Invoice Central Stats section
// totalInvoiceAmount used on Invoice Central navigation items, removes awarded invoices when includeAwardedInvoices is false
// TODO: Remove awarded invoice amount subtraction when clearing api is available
const getBuyerCurrencyStats = ({
  buyer,
  includeAwardedInvoices,
}: {
  buyer: BuyerGroup;
  includeAwardedInvoices: boolean;
}): BuyerCurrencyStats => {
  const accumulator = buyer.takerMarkets.reduce(
    (acc: BuyerCurrencyAccumulator, curr) => {
      acc.eligibleInvoiceAmount +=
        curr.eligibleInvoiceAmount + (includeAwardedInvoices ? curr.awardedInvoiceAmount : 0);
      acc.eligibleDpeWeightedSum +=
        curr.eligibleDpeWeightedSum + (includeAwardedInvoices ? curr.awardedDpeWeightedSum : 0);

      acc.avgDaysPaidEarly = acc.eligibleDpeWeightedSum / acc.eligibleInvoiceAmount;

      // Subtract awarded invoice amount from total invoice amount when not including awarded invoices
      // This is only for Invoice Central while we don't have clearing api to account for awarded invoices
      acc.totalInvoiceAmount += curr.totalInvoiceAmount - (!includeAwardedInvoices ? curr.awardedInvoiceAmount : 0);

      // calculate eligible invoice count as eligible invoice count + awarded invoice count when includeAwardedInvoices is true for current taker market
      const eligibleInvoiceCountForTakerMarket =
        curr.eligibleInvoiceCount + (includeAwardedInvoices ? curr.awardedInvoiceCount : 0);

      // get the soonest marketGoodFundsDate with eligible invoices across all takerMarkets
      if (curr.marketGoodFundsDate && eligibleInvoiceCountForTakerMarket > 0) {
        if (
          !acc.nextGoodFundsDateWithEligibleInvoices ||
          curr.marketGoodFundsDate < acc.nextGoodFundsDateWithEligibleInvoices
        ) {
          acc.nextGoodFundsDateWithEligibleInvoices = curr.marketGoodFundsDate;
        }
      }

      return acc;
    },
    {
      avgDaysPaidEarly: 0,
      eligibleDpeWeightedSum: 0,
      eligibleInvoiceAmount: 0,
      nextGoodFundsDateWithEligibleInvoices: null,
      totalInvoiceAmount: 0,
    }
  );

  const {
    eligibleInvoiceAmount: _eligibleInvoiceAmount,
    eligibleDpeWeightedSum: _eligibleDpeWeightedSum,
    ...stats
  } = accumulator;

  return stats;
};

export interface BuyerCurrencyGroup extends BuyerGroup {
  currency: string;
}

export interface BuyerCurrencyGroupWithStats extends BuyerCurrencyGroup {
  stats: ReturnType<typeof getBuyerCurrencyStats>;
}

const EMPTY_BUYER_GROUPS: BuyerCurrencyGroupWithStats[] = [];

// set includeAwardedInvoices to false in consumers while manual award invoices are not returned for Invoice Central
// Update to remove when clearing invoices api implemented to account for manual awards
// NOTE: stats are in the same currency as the buyer-currency group
const useGroupByBuyerCurrency = ({
  takerMarkets,
  includeAwardedInvoices = true,
}: {
  takerMarkets: TakerMarket[];
  includeAwardedInvoices?: boolean;
}): BuyerCurrencyGroupWithStats[] => {
  return useMemo(() => {
    if (!takerMarkets || takerMarkets.length === 0) {
      return EMPTY_BUYER_GROUPS;
    }

    const buyers = takerMarkets.reduce(
      (acc, takerMarket) => {
        const {
          makerOrganizationUuid,
          makerDivisionId: makerId,
          makerOrganizationLogo,
          makerOrganizationName,
          currency,
        } = takerMarket;
        const buyerCurrencyKey = `${makerOrganizationUuid}-${currency}`;
        const existingBuyerCurrency = acc[buyerCurrencyKey];

        // Ensure sorting excludes awarded invoices when includeAwardedInvoices is false
        const total = includeAwardedInvoices
          ? takerMarket['totalInvoiceAmountUsd']
          : takerMarket['totalInvoiceAmountUsd'] - takerMarket['awardedInvoiceAmountUsd'];

        acc[buyerCurrencyKey] = {
          makerOrganizationUuid,
          makerId,
          makerOrganizationLogo,
          makerOrganizationName,
          currency,
          takerMarkets: existingBuyerCurrency?.takerMarkets
            ? [...existingBuyerCurrency.takerMarkets, takerMarket]
            : [takerMarket],
          total: (existingBuyerCurrency?.total ?? 0) + total, // store total for sorting
        };

        return acc;
      },
      {} as Record<string, BuyerCurrencyGroup & { total: number }>
    );

    return Object.values(buyers)
      .sort((a, b) => b.total - a.total || a.makerOrganizationName.localeCompare(b.makerOrganizationName))
      .map(({ total: _, ...buyer }) => ({
        ...buyer,
        takerMarkets: sortTakerMarkets(buyer.takerMarkets, {
          key: 'totalInvoiceAmount',
          direction: 'desc',
        }),
        stats: getBuyerCurrencyStats({
          buyer,
          includeAwardedInvoices,
        }),
      }));
  }, [takerMarkets, includeAwardedInvoices]);
};

export default useGroupByBuyerCurrency;
